import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, MessageCircle, Mail } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/data/treasures";
import { Ornament } from "@/components/Ornament";
import { MarketplaceButtons } from "@/components/Marketplace";

export const Footer = () => {
  const { t, contact } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="relative bg-espresso-deep text-cream/80 overflow-hidden grain-overlay">
      <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 20% 0%, rgba(200,169,126,0.12), transparent 55%)" }} />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-10">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <img src={IMAGES.logo} alt="Mara Wunderkammer" className="h-12 w-12 rounded-md object-cover ring-1 ring-gold/40" />
              <span className="font-serif text-gold text-xl">Mara Wunderkammer</span>
            </div>
            <p className="text-sm leading-relaxed text-cream/60">{t.footer.tagline}</p>
          </div>

          <div>
            <h4 className="font-serif text-gold text-lg mb-5">{t.nav.contact}</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3"><MapPin className="w-4 h-4 text-gold mt-0.5 shrink-0" /><span>{contact.address}</span></li>
              <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-gold shrink-0" /><a href={`tel:${contact.phoneHref}`} className="hover:text-gold transition-colors" data-testid="footer-phone">{contact.phone}</a></li>
              <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-gold shrink-0" /><a href={contact.emailHref} className="hover:text-gold transition-colors" data-testid="footer-email">{contact.email}</a></li>
              <li className="flex items-start gap-3"><Clock className="w-4 h-4 text-gold mt-0.5 shrink-0" /><span>{t.common.monSat}<br/>{t.common.hours}<br/><span className="text-cream/50">{t.common.sunday}</span></span></li>
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-gold text-lg mb-5">{t.nav.home}</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/cafe" className="hover:text-gold transition-colors">{t.nav.cafe}</Link></li>
              <li><Link to="/brockenstube" className="hover:text-gold transition-colors">{t.nav.brocken}</Link></li>
              <li><Link to="/dienstleistungen" className="hover:text-gold transition-colors">{t.nav.services}</Link></li>
              <li><Link to="/geschichte" className="hover:text-gold transition-colors">{t.nav.story}</Link></li>
              <li><a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-gold transition-colors" data-testid="footer-whatsapp"><MessageCircle className="w-4 h-4" />WhatsApp</a></li>
            </ul>
          </div>

          {/* Trust anchor: Mara Services Gruppe */}
          <div className="rounded-xl border border-gold/25 bg-gold/[0.04] p-6">
            <span className="text-[11px] tracking-[0.3em] uppercase text-gold/70">Mara Services Gruppe</span>
            <p className="mt-3 text-sm leading-relaxed text-cream/70">{t.footer.group}</p>
            <div className="mt-5">
              <span className="block text-[11px] tracking-[0.3em] uppercase text-gold/70 mb-3">{t.market.eyebrow}</span>
              <MarketplaceButtons />
            </div>
          </div>
        </div>

        <Ornament className="my-10 opacity-50" width={260} />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-cream/45">
          <span>© {year} Mara Wunderkammer. {t.footer.rights}</span>
          <span className="order-3 md:order-2">
            Erstellt von{" "}
            <a href="https://alpenpro.agency/" target="_blank" rel="noreferrer" data-testid="footer-agency"
              className="alpen-glow font-medium text-gold hover:text-gold-light transition-colors">
              Alpen Pro Agency
            </a>
          </span>
          <div className="flex items-center gap-6 order-2 md:order-3">
            <Link to="/impressum" className="hover:text-gold transition-colors" data-testid="footer-imprint">{t.footer.legal.imprint}</Link>
            <Link to="/datenschutz" className="hover:text-gold transition-colors" data-testid="footer-privacy">{t.footer.legal.privacy}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
